import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Send } from "lucide-react";
import { toast } from "sonner";
import { Field } from "../../components/Field";
import { Scheduler } from "../../components/Scheduler";
import { Button } from "../../components/ui/button";
import { submitInquiry, errText } from "../../lib/api";
import { track } from "../../lib/analytics";
import { captureAttribution, getAttribution } from "../../lib/attribution";

const resolve = (f, answers) => {
  if (typeof f.help !== "function" && typeof f.options !== "function") return f;
  const out = { ...f };
  if (typeof out.help === "function") out.help = out.help(answers);
  if (typeof out.options === "function") out.options = out.options(answers);
  return out;
};

export const EnquiryForm = ({ config }) => {
  const draftKey = `sabrea_form_${config.slug}_v1`;
  const saved = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(draftKey)) || {};
    } catch {
      return {};
    }
  }, [draftKey]);

  const [answers, setAnswers] = useState(saved);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [booked, setBooked] = useState(false);
  const rootRef = useRef(null);
  const embedded = typeof window !== "undefined" && window.self !== window.top;
  const shell = embedded ? "" : "min-h-screen";
  const pad = embedded ? "px-5 py-8 md:px-8" : "px-6 py-14 md:px-10 md:py-16";

  useEffect(() => {
    captureAttribution();
    track("form_viewed", { form: config.slug });
  }, [config.slug]);

  useEffect(() => {
    localStorage.setItem(draftKey, JSON.stringify(answers));
  }, [answers, draftKey]);

  // Tell a parent page (Squarespace embed) how tall our content is so the iframe can resize.
  useEffect(() => {
    if (!embedded) return;
    const el = rootRef.current;
    if (!el) return;
    let last = 0;
    const post = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      if (Math.abs(h - last) < 24) return;
      last = h;
      window.parent.postMessage({ type: "sabrea-form-height", height: h }, "*");
    };
    post();
    const ro = new ResizeObserver(post);
    ro.observe(el);
    return () => ro.disconnect();
  }, [embedded, answers, done]);

  const fields = config.fields.filter((f) => !f.cond || f.cond(answers)).map((f) => resolve(f, answers));

  const onChange = (id, v) => {
    // Changing country clears a previously chosen state so the pair can never mismatch.
    setAnswers((p) => (id === "country" ? { ...p, country: v, state: undefined } : { ...p, [id]: v }));
    setErrors((p) => ({ ...p, [id]: undefined }));
  };

  const send = async () => {
    const e = {};
    fields.forEach((f) => {
      const v = answers[f.id];
      if (f.required && (!v || (Array.isArray(v) && !v.length))) e[f.id] = "This one is required.";
      if (f.type === "email" && v && !/^\S+@\S+\.\S+$/.test(v)) e[f.id] = "Enter a valid email address.";
    });
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error("A few answers are still missing.");
      return;
    }
    setSending(true);
    try {
      const data = await submitInquiry({
        ...answers,
        inquiry_type: config.inquiryType,
        attribution: getAttribution(),
      });
      localStorage.removeItem(draftKey);
      setDone(data);
      track("form_submitted", { form: config.slug });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div ref={rootRef} className={`form-light ${shell} bg-white ${pad}`}>
        <div className={`rise mx-auto ${config.scheduler ? "max-w-4xl" : "max-w-3xl"}`} data-testid="form-complete">
          <p className="font-mono-tech flex items-center gap-2 text-xs text-green-600">
            <Check className="h-4 w-4" /> Submission received
          </p>
          <h1 className="mt-3 font-display text-3xl font-black leading-tight text-zinc-900 md:text-4xl">
            {config.scheduler ? "Let's book your Artist Discovery Call." : "Thanks — we've got it."}
          </h1>
          <p className="mt-5 text-base leading-relaxed text-zinc-700">
            {config.scheduler
              ? "Pick a time below and we'll walk through where you are, where you want to go, and what should happen next. A confirmation goes to "
              : "Your submission is with the team. We'll reply by email at "}
            <span className="text-brand-pink">{done.contact?.email}</span>
            {config.scheduler ? "." : " with the right next step."}
          </p>
          {config.scheduler && (
            <div className="mt-8" data-testid="discovery-call-scheduler">
              <Scheduler lead={{}} onBooked={() => setBooked(true)} />
              {booked && (
                <p className="font-mono-tech mt-6 text-[11px] text-green-600" data-testid="booking-confirmed">
                  ✓ Your Artist Discovery Call is booked — check your email for the invite.
                </p>
              )}
            </div>
          )}
          <div className="mt-9 rounded-xl border border-zinc-200 bg-zinc-50 p-6">
            <p className="font-mono-tech text-[10px] text-zinc-500">Submission</p>
            <p className="mt-2 font-display text-lg font-black text-brand-pink" data-testid="form-inquiry-type">
              {done.inquiry_type}
            </p>
            <p className="mt-2 text-sm text-zinc-600">{done.display_name}</p>
          </div>
          <button type="button" data-testid="form-another-btn"
            onClick={() => { setDone(null); setBooked(false); setAnswers({}); }}
            className="font-mono-tech mt-9 text-[11px] text-brand-indigo transition-colors duration-200 hover:text-brand-pink">
            Submit another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className={`form-light ${shell} bg-white ${pad}`}>
      <div className="mx-auto max-w-3xl">
        <header className="rise">
          <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500" data-testid="form-eyebrow">
            {config.eyebrow}
          </p>
          <h1 className="mt-3 font-display text-3xl font-black leading-[1.06] text-zinc-900 md:text-4xl">
            {config.title}
          </h1>
          <p className="mt-5 text-base leading-relaxed text-zinc-600">{config.intro}</p>
          <div className="brand-gradient-rule mt-8 h-[2px] w-full rounded-full" />
        </header>

        <div className="mt-8 grid gap-x-10 md:grid-cols-2">
          {fields.map((f) => (
            <div key={f.id} className={f.half ? "md:col-span-1" : "md:col-span-2"}>
              <Field field={f} value={answers[f.id]} onChange={onChange} error={errors[f.id]} />
            </div>
          ))}
        </div>

        <Button type="button" data-testid="form-submit-btn" disabled={sending} onClick={send}
          className="h-13 w-full rounded-full bg-brand-pink py-3.5 text-base font-semibold text-zinc-950 transition-colors duration-200 hover:bg-brand-pink-soft sm:w-auto sm:px-10">
          <Send className="mr-2 h-4 w-4" /> {sending ? "Submitting…" : "Submit"}
        </Button>

        <p className="font-mono-tech mt-10 text-[10px] leading-relaxed text-zinc-500">
          We only use your details to respond to this submission.
        </p>
      </div>
    </div>
  );
};
